import { NO_RISK } from "./strategyContext.js";
const POINT_KINDS = ["ui", "di", "ao", "do", "relay"];
export function buildControllerPointLedger(input) {
    const ownedZoneIds = new Set(input.zoneSlots.flatMap((slot) => [
        slot.logicalZoneId,
        ...extractCombinedZoneIds(slot.notes)
    ]));
    const entries = input.pointDemands
        .filter((demand) => ownedZoneIds.has(demand.ownerRef))
        .flatMap((demand) => entriesFromDemand(`ledger-entry-${input.controllerId}`, demand));
    return buildLedger({
        id: `point-ledger-${input.controllerId}`,
        entries,
        available: input.capacity,
        sourceRefs: input.sourceRefs,
        ruleTraceRefs: input.ruleTraceRefs,
        why: "Controller point ledger proves the selected hardware is not over capacity.",
        how: "Summed active hard-IO point demands owned by this controller and compared them with catalog capacity.",
        suggestRelayAsDo: true
    });
}
export function buildApartmentPointLedger(input) {
    const entries = input.pointDemands.flatMap((demand) => entriesFromDemand(`ledger-entry-${input.apartmentTypeId}`, demand));
    const available = sumPoints(input.controllers.map((controller) => controller.capacity));
    return buildLedger({
        id: `point-ledger-${input.apartmentTypeId}`,
        entries,
        available,
        sourceRefs: input.sourceRefs,
        ruleTraceRefs: input.ruleTraceRefs,
        why: "Apartment point ledger rolls controller and load demand into an apartment-type IO budget.",
        how: "Summed every active hard-IO point demand and compared it with the selected controller capacity rollup.",
        includeAllEntriesInRequired: true
    });
}
export function buildLedger(input) {
    const required = sumEntries(input.entries.filter((entry) => input.includeAllEntriesInRequired
        ? entry.activeState === "active"
        : entry.consumesHardIo && entry.activeState === "active"));
    const reserved = zeroPoints();
    const spare = subtractNonNegative(input.available, required);
    const violations = POINT_KINDS.flatMap((pointKind) => required[pointKind] > input.available[pointKind]
        ? [
            {
                pointKind,
                required: required[pointKind],
                available: input.available[pointKind],
                message: `${pointKind.toUpperCase()} required ${required[pointKind]} exceeds available ${input.available[pointKind]}.`
            }
        ]
        : []);
    const suggestions = input.suggestRelayAsDo
        ? makeRelayAsDoSuggestions({
            ledgerId: input.id,
            entries: input.entries,
            available: input.available,
            required,
            spare
        })
        : [];
    return {
        id: input.id,
        required,
        available: input.available,
        reserved,
        spare,
        violations,
        ...(suggestions.length > 0 ? { suggestions } : {}),
        entries: [...input.entries],
        why: input.why,
        how: input.how,
        sourceRefs: input.sourceRefs,
        ruleTraceRefs: input.ruleTraceRefs,
        risk: violations.length === 0
            ? NO_RISK
            : {
                level: "blocker",
                riskRefs: violations.map((violation) => `risk-${input.id}-${violation.pointKind}`),
                notes: "Ledger has hard-IO violations and must not be accepted without a deterministic remedy."
            }
    };
}
export function entriesFromDemand(idPrefix, demand) {
    return POINT_KINDS.flatMap((pointKind) => {
        const quantity = demand.points[pointKind];
        if (quantity === 0) {
            return [];
        }
        return [
            {
                id: `${idPrefix}-${demand.id}-${pointKind}`,
                ownerRef: demand.ownerRef,
                pointKind,
                quantity,
                consumesHardIo: demand.consumesHardIo,
                activeState: demand.activeState,
                why: demand.why,
                how: demand.how,
                sourceRefs: demand.sourceRefs,
                ruleTraceRefs: demand.ruleTraceRefs,
                risk: demand.risk
            }
        ];
    });
}
export function sumPoints(vectors) {
    return vectors.reduce((total, vector) => ({
        ui: total.ui + vector.ui,
        di: total.di + vector.di,
        ao: total.ao + vector.ao,
        do: total.do + vector.do,
        relay: total.relay + vector.relay
    }), zeroPoints());
}
function sumEntries(entries) {
    return entries.reduce((total, entry) => {
        if (entry.pointKind === "ui") {
            return { ...total, ui: total.ui + entry.quantity };
        }
        if (entry.pointKind === "di") {
            return { ...total, di: total.di + entry.quantity };
        }
        if (entry.pointKind === "ao") {
            return { ...total, ao: total.ao + entry.quantity };
        }
        if (entry.pointKind === "do") {
            return { ...total, do: total.do + entry.quantity };
        }
        return { ...total, relay: total.relay + entry.quantity };
    }, zeroPoints());
}
function subtractNonNegative(left, right) {
    return {
        ui: Math.max(0, left.ui - right.ui),
        di: Math.max(0, left.di - right.di),
        ao: Math.max(0, left.ao - right.ao),
        do: Math.max(0, left.do - right.do),
        relay: Math.max(0, left.relay - right.relay)
    };
}
function makeRelayAsDoSuggestions(input) {
    const doEntries = input.entries.filter((entry) => entry.pointKind === "do" &&
        entry.activeState === "active" &&
        entry.consumesHardIo &&
        entry.quantity > 0);
    if (input.required.do <= input.available.do ||
        input.spare.relay === 0 ||
        doEntries.length === 0) {
        return [];
    }
    const candidate = doEntries[doEntries.length - 1];
    if (!candidate) {
        return [];
    }
    const riskRef = `risk-${input.ledgerId}-relay-as-do-${normalizeId(candidate.ownerRef)}`;
    return [
        {
            id: `suggestion-${input.ledgerId}-relay-as-do-${normalizeId(candidate.ownerRef)}`,
            suggestionKind: "relay_as_do",
            severity: "info",
            ownerRef: candidate.ownerRef,
            pointKind: "do",
            message: `Controller ${input.ledgerId.replace(/^point-ledger-/, "")} DOs are full; a spare relay could be repurposed as a DO (24V through the relay) to drive ${candidate.ownerRef}.`,
            recommendedAction: "Confirm whether to repurpose a spare controller relay as a digital-output-style enable before changing the IO allocation.",
            riskRef,
            sourceRefs: candidate.sourceRefs,
            ruleTraceRefs: candidate.ruleTraceRefs
        }
    ];
}
function normalizeId(value) {
    return value
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, "-")
        .replace(/^-|-$/g, "");
}
function zeroPoints() {
    return {
        ui: 0,
        di: 0,
        ao: 0,
        do: 0,
        relay: 0
    };
}
function extractCombinedZoneIds(notes) {
    if (!notes?.includes("Combined load zones:")) {
        return [];
    }
    const [, combinedText] = notes.split("Combined load zones:");
    if (!combinedText) {
        return [];
    }
    return combinedText
        .replace(".", "")
        .split(",")
        .map((zoneId) => zoneId.trim())
        .filter(Boolean);
}
//# sourceMappingURL=pointLedger.js.map